// Remembers which conversation the user was last in, so backgrounding the app
// or a reload (mobile browsers evict suspended pages) reopens the same chat
// instead of dropping to a blank "new chat". Stored in localStorage alongside
// the auth tokens (see tokenStore.ts) with a timestamp; entries older than the
// resume window are treated as expired and the user starts fresh — the old
// conversation is still reachable from the history drawer.

const KEY = "cc_active_conversation";

// Returning within this window reopens the conversation; after it, a new chat.
export const RESUME_WINDOW_MS = 30 * 60_000;

interface Entry {
  id: string;
  ts: number;
}

function read(): Entry | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry;
    return parsed && typeof parsed.id === "string" && typeof parsed.ts === "number" ? parsed : null;
  } catch {
    return null;
  }
}

function write(entry: Entry): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
}

export const activeConversation = {
  /** Remember the conversation the user is in, stamped with the current time. */
  save(conversationId: string): void {
    write({ id: conversationId, ts: Date.now() });
  },

  /** The stored conversation id if it's within `maxAgeMs`; otherwise clears and returns null. */
  load(maxAgeMs: number = RESUME_WINDOW_MS): string | null {
    const entry = read();
    if (!entry) return null;
    if (Date.now() - entry.ts >= maxAgeMs) {
      this.clear();
      return null;
    }
    return entry.id;
  },

  /** Re-stamp the current entry's timestamp (on activity or when leaving). */
  touch(): void {
    const entry = read();
    if (entry) write({ id: entry.id, ts: Date.now() });
  },

  clear(): void {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* non-fatal */
    }
  }
};
